/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import { assign, createMachine } from 'xstate';
import ExtJS from '../../../../../../../interface/ExtJS';
import { fetchQuarantine, fetchQuarantineSummary } from '../quarantine.api';
import { QUARANTINE_STRINGS as STRINGS } from '../quarantine.strings';
import {
  DEFAULT_QUERY,
  EMPTY_FILTERS,
  type FilterSection,
  type QuarantinePage,
  type QuarantineQuery,
  type QuarantineSortField,
  type QuarantineSummary,
} from '../quarantine.types';

export interface QuarantineListContext {
  query: QuarantineQuery;
  data: QuarantinePage | null;
  error: string | null;
  // The "actively in quarantine" count is independent of the query; null until loaded or if the lookup fails.
  summary: QuarantineSummary | null;
}

export type QuarantineListEvent =
  | { type: 'SET_FILTER'; section: FilterSection; values: string[] }
  | { type: 'CLEAR_FILTERS' }
  | { type: 'CLEAR_ALL' }
  | { type: 'SET_SEARCH'; search: string }
  | { type: 'SET_SORT'; sortBy: QuarantineSortField; asc: boolean }
  | { type: 'SET_PAGE'; page: number }
  | { type: 'SET_PAGE_SIZE'; pageSize: number }
  | { type: 'RETRY' };

type EventOf<T extends QuarantineListEvent['type']> = Extract<QuarantineListEvent, { type: T }>;

const withQuery = (context: QuarantineListContext, patch: Partial<QuarantineQuery>): QuarantineQuery => ({
  ...context.query,
  ...patch,
  page: patch.page ?? 1,
});

const setFilter = assign<QuarantineListContext, EventOf<'SET_FILTER'>>({
  query: (context, event) =>
    withQuery(context, { filters: { ...context.query.filters, [event.section]: event.values } }),
});
const clearFilters = assign<QuarantineListContext, EventOf<'CLEAR_FILTERS'>>({
  query: (context) => withQuery(context, { filters: { ...EMPTY_FILTERS } }),
});
const clearAll = assign<QuarantineListContext, EventOf<'CLEAR_ALL'>>({
  query: (context) => withQuery(context, { filters: { ...EMPTY_FILTERS }, search: '' }),
});
const setSearch = assign<QuarantineListContext, EventOf<'SET_SEARCH'>>({
  query: (context, event) => withQuery(context, { search: event.search }),
});
const setSort = assign<QuarantineListContext, EventOf<'SET_SORT'>>({
  query: (context, event) => withQuery(context, { sortBy: event.sortBy, asc: event.asc }),
});
const setPage = assign<QuarantineListContext, EventOf<'SET_PAGE'>>({
  query: (context, event) => withQuery(context, { page: event.page }),
});
const setPageSize = assign<QuarantineListContext, EventOf<'SET_PAGE_SIZE'>>({
  query: (context, event) => withQuery(context, { pageSize: event.pageSize }),
});

const setLoaded = assign<QuarantineListContext, { type: 'done.invoke.fetchQuarantine'; data: QuarantinePage }>({
  data: (_context, event) => event.data,
  error: () => null,
});
const setFailed = assign<QuarantineListContext, { type: 'error.platform.fetchQuarantine'; data: unknown }>({
  data: () => null,
  error: (_context, event) =>
    event.data instanceof Error ? event.data.message : STRINGS.errors.loadList,
});

const setSummary = assign<QuarantineListContext, { type: 'done.invoke.fetchSummary'; data: QuarantineSummary }>({
  summary: (_context, event) => event.data,
});
const clearSummary = assign<QuarantineListContext>({ summary: () => null });

// Handled in every state: re-entering `loading` cancels the in-flight request, so a stale response is ignored.
const queryChanges = {
  SET_FILTER: { target: 'loading', actions: 'setFilter' },
  CLEAR_FILTERS: { target: 'loading', actions: 'clearFilters' },
  CLEAR_ALL: { target: 'loading', actions: 'clearAll' },
  SET_SEARCH: { target: 'loading', actions: 'setSearch', cond: 'searchChanged' },
  SET_SORT: { target: 'loading', actions: 'setSort' },
  SET_PAGE: { target: 'loading', actions: 'setPage', cond: 'pageChanged' },
  SET_PAGE_SIZE: { target: 'loading', actions: 'setPageSize' },
};

export const quarantineListMachine = createMachine<QuarantineListContext, QuarantineListEvent>(
  {
    id: 'quarantineList',
    predictableActionArguments: true,
    initial: 'loading',
    context: { query: DEFAULT_QUERY, data: null, error: null, summary: null },
    // Runs once for the machine's lifetime, independent of the list's loading/loaded/failed states.
    invoke: {
      id: 'fetchSummary',
      src: 'fetchSummary',
      onDone: { actions: 'setSummary' },
      onError: { actions: 'clearSummary' },
    },
    states: {
      loading: {
        invoke: {
          id: 'fetchQuarantine',
          src: 'fetchQuarantine',
          onDone: { target: 'loaded', actions: 'setLoaded' },
          onError: { target: 'failed', actions: 'setFailed' },
        },
        on: queryChanges,
      },
      loaded: { on: queryChanges },
      failed: { on: { ...queryChanges, RETRY: 'loading' } },
    },
  },
  {
    services: {
      fetchSummary: () => fetchQuarantineSummary(),
      fetchQuarantine: async (context) => {
        await ExtJS.waitForPermissions();
        return fetchQuarantine(context.query);
      },
    },
    guards: {
      searchChanged: (context, event) => event.type === 'SET_SEARCH' && event.search !== context.query.search,
      pageChanged: (context, event) => event.type === 'SET_PAGE' && event.page !== context.query.page,
    },
    actions: { setFilter, clearFilters, clearAll, setSearch, setSort, setPage, setPageSize, setLoaded, setFailed, setSummary, clearSummary },
  }
);
